import { Injectable } from '@angular/core';
import { first, map, Observable } from 'rxjs';
import {
  EDI_MAPPING_MODELLER_FEATURE_ID,
  EDI_MAPPING_MODELLER_PROTOCOL_VERSION
} from '../../models/edi-mapping-modeller';
import { BackendFeatureService } from '../backend-feature.service';

export interface EdiMappingModellerCompatibility {
  compatible: boolean;
  frontendVersion: string;
  backendVersion?: string;
  updateTier?: 'frontend' | 'backend';
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class EdiMappingModellerCompatibilityService {
  constructor(private backendFeatureService: BackendFeatureService) {}

  getCompatibility(): Observable<EdiMappingModellerCompatibility> {
    return this.backendFeatureService.getAvailableFeatures().pipe(
      first(),
      map(features => {
        const backendVersion = features[EDI_MAPPING_MODELLER_FEATURE_ID]?.version;
        const frontend = this.semanticVersion(EDI_MAPPING_MODELLER_PROTOCOL_VERSION);
        const backend = this.semanticVersion(backendVersion);
        if (!backend) {
          return this.mismatch(backendVersion, 'backend');
        }
        if (backend[0] > frontend[0]) return this.mismatch(backendVersion, 'frontend');
        if (backend[0] < frontend[0] || this.compare(backend, frontend) < 0) {
          return this.mismatch(backendVersion, 'backend');
        }
        return {
          compatible: true,
          frontendVersion: EDI_MAPPING_MODELLER_PROTOCOL_VERSION,
          backendVersion
        };
      })
    );
  }

  private mismatch(
    backendVersion: string | undefined,
    updateTier: 'frontend' | 'backend'
  ): EdiMappingModellerCompatibility {
    const backendDescription = backendVersion || 'not reported';
    return {
      compatible: false,
      frontendVersion: EDI_MAPPING_MODELLER_PROTOCOL_VERSION,
      backendVersion,
      updateTier,
      message: `EDI mapping modeller version mismatch. Backend is ${backendDescription} and frontend is ${EDI_MAPPING_MODELLER_PROTOCOL_VERSION}. Update the ${updateTier} tier.`
    };
  }

  private semanticVersion(version?: string): [number, number, number] | undefined {
    const match = version?.trim().match(/^(\d+)\.(\d+)\.(\d+)(?:[-+].*)?$/);
    return match ? [Number(match[1]), Number(match[2]), Number(match[3])] : undefined;
  }

  private compare(first: [number, number, number], second: [number, number, number]): number {
    for (let index = 0; index < first.length; index++) {
      if (first[index] !== second[index]) return first[index] - second[index];
    }
    return 0;
  }
}
